import {
    Box,
    MenuItem,
    InputAdornment
} from "@mui/material";
import { CustomCheckbox } from "../../../../components/CustomCheckbox";
import DivContainerBody from "../../../../components/DivContainerBody";
import React, {useEffect} from "react";
import { CustomTextField } from "../../../../components/CustomTextField";
import { CustomSelectAndChildren } from "../../../../components/CustomSelectAndChildren";
import {
    AUTHENTICATION_MODE,
    DEVICE_MODEL_NAME,
    DISABLE_CAMERA_ANTENNA,
    STEPS_CREATE_DEVICE
} from "../Model/Model";
import {DISABLE_DISTANCE} from "../Model/DeviceValidator";
import ButtonIcon from "../../../../components/buttons/ButtonIcon";
import {CleanIcon} from "../../../../components/buttons/IconButtons";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTitle: Full Stack Developer
 *
 * Device authentication section.
 * Options are enabled or disabled based on the device type.
 *
 * - disableAuthCommon: Device types for which common authentication options (QR, Bluetooth, smartCard, and PIN) are disabled.
 * - disableGroup1: Device types for which facial recognition is disabled.
 * - disableGroup2: Device types for which fingerprint reader is disabled.
 * - disableAuthSelect: Device types for which the authentication select is disabled.
 *
 * The "readAfter" field validates both empty fields and helper text based on the device type.
 *
 * @param {object} props - Component properties.
 * @param {object} props.data - Object containing device data, including deviceType.
 * @param {function} props.handleChange - Function to handle input changes.
 * @param {function} props.t - Function for text translation.
 * @param {boolean} props.emptyFields - Flag to indicate if empty fields should be highlighted.
 * @param {array} props.deviceTypeAuthentications - Array of available authentication types.
 * @param {boolean} props.formSubmitted - Flag to indicate if the form has been submitted.
 * @param deviceTypeDistance  - type of distance for every device
 * @param defaultDistance - default content of distance input
 * @returns {JSX.Element} Rendered component.
 */

/**
 * Component used to configure
 * device authentication settings.
 *
 * This component handles:
 * - Authentication method configuration.
 * - Facial recognition configuration.
 * - Fingerprint configuration.
 * - QR configuration.
 * - RFID configuration.
 * - PIN configuration.
 * - Distance validation.
 * - Liveness validation.
 * - Camera type selection.
 * - Device-dependent authentication restrictions.
 *
 * @component SectionCreateDeviceAuthentication
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.data - Device form data.
 * @param {Function} props.handleChange - Updates form field values.
 * @param {Function} props.t - Translation function.
 * @param {boolean} props.emptyFields - Indicates validation errors.
 * @param {number|string|undefined} props.id - Device identifier.
 * @param {Function} props.handleClear - Clears authentication step values.
 * @param {Array<number>} props.deviceTypeDistance - Allowed distance values.
 * @param {number} props.defaultDistance - Default distance value.
 *
 * @returns {JSX.Element} Authentication configuration section.
 */
const SectionCreateDeviceAuthentication = ({data, handleChange, t, emptyFields
                                               , id,handleClear,
                                               deviceTypeDistance, defaultDistance}) => {
    // Conditional control arrays
    const disabledIR = [
        DEVICE_MODEL_NAME.ARATEK_BM7550,
        DEVICE_MODEL_NAME.TELPO_K10,
        DEVICE_MODEL_NAME.ARATEK_BM5510]

    // Device types for which facial recognition is disabled.
    const disableGroup1 = [
        DEVICE_MODEL_NAME.TELPO_TPS_450,
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR,
    ];
    // Device types for which the fingerprint reader is disabled.
    const disableGroup2 = [
        DEVICE_MODEL_NAME.TELPO_K10,
        DEVICE_MODEL_NAME.TELPO_K5,
        DEVICE_MODEL_NAME.TELPO_F10,
        DEVICE_MODEL_NAME.CAMERA_LPR,
        DEVICE_MODEL_NAME.ANTENNA_RFID];

    const isDisabled = DISABLE_CAMERA_ANTENNA.includes(data?.data?.deviceType);

    const isDistanceDisabled =
        disableGroup1.includes(data?.data?.deviceType) ||
        data?.authentication?.facialRecognition !== true;

    const isLivenessEnabled =
        !disableGroup1.includes(data?.data?.deviceType) &&
        data?.authentication?.facialRecognition === true;

    const isDistanceAllowed = (deviceType, enableDistance) => {
        if (!enableDistance) return false;
        return !DISABLE_DISTANCE.includes(deviceType)
    };

    useEffect(() => {

        const facialDisabled =
            disableGroup1.includes(data?.data?.deviceType) ||
            data?.authentication?.facialRecognition !== true;

        // Reset enableDistance
        if (facialDisabled && data?.authentication?.enableDistance) {
            handleChange(
                { target: { value: false } },
                'enableDistance',
                'authentication'
            );
        }

        // Reset distance to default
        if (
            (!data?.authentication?.enableDistance || facialDisabled) &&
            data?.authentication?.distance !== defaultDistance
        ) {
            handleChange(
                { target: { value: defaultDistance } },
                'distance',
                'authentication'
            );
        }

        // Reset liveness
        if (facialDisabled && data?.authentication?.liveness) {
            handleChange(
                { target: { value: false } },
                'liveness',
                'authentication'
            );
        }

        // Reset cameraTypeId
        if (
            (facialDisabled || !data?.authentication?.liveness) &&
            data?.authentication?.cameraTypeId !== 0
        ) {
            handleChange(
                { target: { value: 0 } },
                'cameraTypeId',
                'authentication'
            );
        }

    }, [
        data?.data?.deviceType,
        data?.authentication?.facialRecognition,
        data?.authentication?.enableDistance,
        data?.authentication?.liveness
    ]);

    // Resets cameraTypeId when liveness is turned off
    useEffect(() => {
        if (!data?.authentication.liveness && data?.authentication.cameraTypeId !== 0) {
            handleChange({ target: { name: 'cameraTypeId', value: 0 } });
        }
    }, [data?.authentication.liveness]);

    useEffect(() => {
        if (isDisabled && data?.authentication?.readAfter !== 0) {
            handleChange(
                { target: { value: 0 } },
                "readAfter",
                'authentication'
            );
        }
    }, [data?.data?.deviceType]);

    return (
        <>
            <DivContainerBody>
                <div className={"row"}>
                    <div className='d-flex justify-content-end col-12 mb-2'>
                        {id !== undefined ? (
                            ''
                        ) : (
                            <ButtonIcon
                                className='btn-clean-secondary-600'
                                id='btn_cud_clean_all_inputs'
                                Icon={CleanIcon}
                                title={'clear_instances'}
                                onClick={() => {
                                    handleClear(STEPS_CREATE_DEVICE.AUTHENTICATION.INDEX);
                                }}
                                iconProps='btn-icon-svg-12px icon-color-general-00'
                            />
                        )}
                    </div>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_facial_recognition"
                            checked={data?.authentication.facialRecognition}
                            value={data?.authentication.facialRecognition}
                            onChange={(evt) => { handleChange(evt, "facialRecognition", 'authentication') }}
                            labelId="lbl_cud_facialRecognition"
                            label="facial_recognition"
                            trans="facial_recognition"
                            name="facialRecognition"
                            disabled={disableGroup1.includes(data?.data?.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_fingerprint"
                            checked={data?.authentication.fingerprint}
                            value={data?.authentication.fingerprint}
                            onChange={(evt) => { handleChange(evt, "fingerprint", 'authentication') }}
                            labelId="lbl_cud_fingerprint"
                            label="fingerprint_reader"
                            trans="fingerprint_reader"
                            name="fingerprint"
                            disabled={disableGroup2.includes(data?.data?.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_qr"
                            checked={data?.authentication.qr}
                            value={data?.authentication.qr}
                            onChange={(evt) => { handleChange(evt, "qr", 'authentication') }}
                            labelId="lbl_cud_qr"
                            label="qr_code_reader"
                            trans="qr_code_reader"
                            name="qr"
                            disabled={DISABLE_CAMERA_ANTENNA.includes(data?.data?.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mb-0">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_bluetooth"
                            checked={data?.authentication.bluetooth}
                            value={data?.authentication.bluetooth}
                            onChange={(evt) => { handleChange(evt, "bluetooth", 'authentication') }}
                            labelId="lbl_cud_bluetooth"
                            label="bluetooth_reader"
                            trans="bluetooth_reader"
                            name="bluetooth"
                            disabled={true}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_smart_card"
                            checked={data?.authentication.smartCard}
                            value={data?.authentication.smartCard}
                            onChange={(evt) => { handleChange(evt, "smartCard", 'authentication') }}
                            labelId="lbl_cud_smartCard"
                            label="rfid_reader"
                            trans="rfid_reader"
                            name="smartCard"
                            disabled={DISABLE_CAMERA_ANTENNA.includes(data?.data?.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <CustomCheckbox
                            marginbutton={true}
                            id="cb_cud_pin"
                            checked={data?.authentication.pin}
                            value={data?.authentication.pin}
                            onChange={(evt) => { handleChange(evt, "pin", 'authentication') }}
                            labelId="lbl_cud_pin"
                            label="pin"
                            trans="pin"
                            name="pin"
                            disabled={DISABLE_CAMERA_ANTENNA.includes(data?.data?.deviceType)}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3">
                        <CustomTextField
                            id="txt_cud_read_again_after"
                            name="readAfter"
                            emptyFields={emptyFields && !isDisabled}
                            trans="read_again_after"
                            required={!isDisabled}
                            value={isDisabled ? 0 : (data?.authentication?.readAfter ?? '')}
                            type = 'number'
                            onBlur={() => {
                                if (isDisabled) {
                                    handleChange(
                                        { target: { value: 0 } },
                                        "readAfter",
                                        'authentication'
                                    );
                                }
                            }}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (isDisabled) {
                                    handleChange(
                                        { target: { value: 0 } },
                                        "readAfter",
                                        'authentication'
                                    );
                                    return;
                                }

                                if (value === '' || Number(value) >= 1) {
                                    handleChange(e, "readAfter", 'authentication');
                                }
                            }}
                            disabled={isDisabled}
                            inputProps={{
                                min: isDisabled ? 0 : 1,
                                endAdornment: (
                                    <InputAdornment className="endAdornmentStyle" position="end">
                                        {t("sec")}
                                    </InputAdornment>
                                ),
                            }}
                            length={4}
                            helperText={t("this_value_must_be_greater_than_0")}
                        />
                    </Box>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3">
                        <CustomSelectAndChildren
                            id="txt_cud_authentication"
                            name="authentication"
                            value={data?.authentication?.authentication || AUTHENTICATION_MODE[0].name}
                            trans="authentication_method"
                            disabled={DISABLE_CAMERA_ANTENNA.includes(data?.data?.deviceType)}
                            onChange={(evt) => { handleChange(evt, "authentication", 'authentication') }}
                            showNoSelectionOption={false}
                        >
                            {AUTHENTICATION_MODE.map((item) => (
                                <MenuItem key={item.id} value={item.name}>
                                    {t(item.trans)}
                                </MenuItem>
                            ))}
                        </CustomSelectAndChildren>
                    </Box>
                    <div className={"col-6 row p-0"}>
                       <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                           <CustomCheckbox
                               marginbutton={true}
                               id="cb_cud_enable_distance"
                               checked={data?.authentication.enableDistance}
                               onChange={(e) =>    handleChange(e, 'enableDistance', 'authentication')}
                               labelId={"lbl_cud_enable_distance"}
                               label={"enable_distance"}
                               trans={"enable_distance"}
                               name={"enableDistance"}
                               disabled={isDistanceDisabled}
                           />
                       </Box>
                       <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                           <CustomSelectAndChildren
                               id="slt_cud_distance"
                               name="distance"
                               required={data?.authentication.enableDistance === true}
                               trans="distance"
                               label="lbl_cud_distance"
                               showNoSelectionOption={false}
                               value={data?.authentication.enableDistance ? (data?.authentication.distance ?? '') : defaultDistance}
                               onChange={(e) =>    handleChange(e, 'distance', 'authentication')}
                               InputProps={{
                                   endAdornment: (
                                       <InputAdornment className="endAdornmentStyle" position="end" />
                                   ),
                               }}
                               emptyFields={data?.authentication.enableDistance === true ?? emptyFields}
                               disabled={!isDistanceAllowed(data?.data?.deviceType, data?.authentication.enableDistance)}
                           >
                               {deviceTypeDistance.map((value) => (
                                   <MenuItem key={value} value={value}>
                                       {`${value} CM`}
                                   </MenuItem>
                               ))}
                           </CustomSelectAndChildren>
                       </Box>
                   </div>
                    <Box className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
                        <CustomTextField
                            id="txt_cud_consecutive_openings_allowed"
                            name="consecutiveOpeningsAllowed"
                            trans="number_attemps"
                            value={data?.authentication?.consecutiveOpeningsAllowed ?? 0}
                            type = 'number'
                            onBlur={(e) => {
                                if (e.target.value === '') {
                                    handleChange(
                                        { target: { value: 0 } },
                                        "consecutiveOpeningsAllowed",
                                        'authentication'
                                    );
                                }
                            }}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || Number(value) >= 0) {
                                    handleChange(e, "consecutiveOpeningsAllowed", 'authentication');
                                }
                            }}
                            lenght={4}
                            disabled={isDisabled}
                            helperText={"zero_means_unlimited"}
                            showHelperTextAlways={data?.authentication?.consecutiveOpeningsAllowed !== ''}
                            InputProps={{
                                min: 0,
                                endAdornment: (
                                    <InputAdornment className="endAdornmentStyle" position="end" />
                                ),
                            }}
                        />
                    </Box>
                    <div className={"col-6 row p-0"}>
                        <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3'>
                            <CustomCheckbox
                                marginbutton={true}
                                id='cb_cud_liveness'
                                checked={data?.authentication.liveness}
                                onChange={(e) =>
                                    handleChange(e, "liveness", 'authentication')
                                }
                                labelId={"lbl_cud_liveness"}
                                label={'liveness'}
                                trans={'liveness'}
                                name={'liveness'}
                                disabled={!isLivenessEnabled}
                            />
                        </Box>
                        <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-3'>
                            <CustomSelectAndChildren
                                id='slt_cud_camera_type'
                                name='cameraTypeId'
                                value={data?.authentication?.cameraTypeId}
                                trans='camera_type_id'
                                onChange={(evt) => { handleChange(evt, "cameraTypeId", 'authentication') }}
                                disabled={!data?.authentication.liveness || disableGroup1.includes(data?.data?.deviceType)}
                                showNoSelectionOption={false}
                            >
                                <MenuItem value={0}>{t('RGB')}</MenuItem>
                                {!disabledIR.includes(data?.data?.deviceType)
                                && <MenuItem value={1}>{t('RGB + IR')}</MenuItem>}
                            </CustomSelectAndChildren>
                        </Box>
                    </div>
                </div>
            </DivContainerBody>
        </>
    );
};

export default SectionCreateDeviceAuthentication;
